import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { HERO, HOME_FEATURES, SERVICES_PAGE } from '../data/content';
import { Helmet } from 'react-helmet-async';

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Helmet>
        <title>Mosolytorna | Peredi Krisztina Logopédus Dunakeszi</title>
        <meta name="description" content="Logopédiai fejlesztés, vizsgálat és terápia gyermekeknek és felnőtteknek Dunakeszin. Személyre szabott foglalkozások 2004 óta." />
      </Helmet>
      
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-100 via-orange-50 via-yellow-50 to-cyan-100 overflow-hidden" aria-labelledby="hero-heading">
        
        {/* --- HALVÁNY, ASSZIMMETRIKUS MOSOLYGÓS FEJEK A HÁTTÉRBEN --- */}
        {/* 1. Bal felső, kicsit balra döntve */}
        <img 
          src="/favicon.svg" 
          alt="" 
          className="absolute -top-10 -left-20 w-80 h-80 md:w-[28rem] md:h-[28rem] opacity-[0.07] -rotate-12 pointer-events-none select-none" 
          aria-hidden="true" 
        />
        
        {/* 2. Jobb középső, erősen jobbra döntve */}
        <img 
          src="/favicon.svg" 
          alt="" 
          className="absolute top-1/3 -right-24 w-96 h-96 md:w-[32rem] md:h-[32rem] opacity-[0.05] rotate-45 pointer-events-none select-none" 
          aria-hidden="true" 
        />
        
        {/* 3. Bal alsó, kicsit jobbra döntve */}
        <img 
          src="/favicon.svg" 
          alt="" 
          className="absolute -bottom-20 left-1/4 w-72 h-72 md:w-[24rem] md:h-[24rem] opacity-[0.06] rotate-12 pointer-events-none select-none" 
          aria-hidden="true" 
        />
        {/* ----------------------------------------------------------- */}

        {/* Eredeti színes színfoltok */}
        <div className="absolute top-20 left-10 w-48 md:w-72 h-48 md:h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse pointer-events-none" aria-hidden="true" />
        <div className="absolute top-40 right-10 w-48 md:w-72 h-48 md:h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse pointer-events-none" aria-hidden="true" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 left-1/3 w-48 md:w-72 h-48 md:h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse pointer-events-none" aria-hidden="true" style={{ animationDelay: '2s' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-28 md:py-24 text-center w-full">
          {/* Profile Photo */}
          <div className="mb-6 md:mb-8 flex justify-center animate-fade-in-up">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-400 via-orange-400 to-yellow-400 rounded-full blur-2xl opacity-40 scale-110" aria-hidden="true" />
              <img
                src="/profile.png"
                alt="Peredi Krisztina profilképe"
                data-testid="hero-profile-image"
                className="relative w-36 h-36 sm:w-44 sm:h-44 md:w-56 md:h-56 rounded-full object-cover shadow-2xl border-4 border-white"
              />
            </div>
          </div>

          <h1
            id="hero-heading"
            className="mb-3 md:mb-4 text-4xl sm:text-5xl md:text-6xl font-light text-gray-800 animate-fade-in-up stagger-1"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-name"
          >
            {HERO.name}
          </h1>

          <p className="text-sm sm:text-base md:text-lg text-orange-600 font-medium mb-5 md:mb-6 px-2 animate-fade-in-up stagger-2 leading-snug">
            {HERO.title}
          </p>

          <div className="max-w-2xl mx-auto text-sm sm:text-base md:text-lg text-gray-700 mb-8 md:mb-10 leading-relaxed animate-fade-in-up stagger-3 px-2">
            <p>{HERO.description}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-fade-in-up stagger-4 px-4 sm:px-0">
            <Link
              to={HERO.ctaPrimary.href}
              data-testid="hero-cta-primary"
              className="px-7 py-3.5 bg-gradient-to-r from-pink-500 to-orange-500 text-white rounded-full font-medium hover:from-pink-600 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 inline-flex items-center justify-center gap-2 no-underline"
            >
              {HERO.ctaPrimary.label}
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link
              to={HERO.ctaSecondary.href}
              data-testid="hero-cta-secondary"
              className="px-7 py-3.5 border-2 border-orange-400 text-orange-600 rounded-full font-medium hover:bg-orange-50 transition-all duration-200 hover:scale-105 text-center no-underline"
            >
              {HERO.ctaSecondary.label}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 animate-bounce" aria-hidden="true">
          <ChevronDown className="w-6 h-6 text-orange-400" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-20 bg-white" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2
              id="features-heading"
              className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-800 mb-3 md:mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Miért válasszon engem?
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
              Több mint 20 éves tapasztalattal állok rendelkezésére a logopédiai fejlesztés területén.
            </p>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 list-none p-0 m-0">
            {HOME_FEATURES.map((feature, i) => {
              const FeatureIcon = feature.Icon;
              return (
                <li
                  key={i}
                  className={`bg-gradient-to-br ${feature.gradient} p-5 md:p-6 rounded-3xl text-center shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-1`}
                  data-testid={`feature-card-${i}`}
                >
                  <FeatureIcon className={`w-10 h-10 md:w-12 md:h-12 ${feature.iconColor} mx-auto mb-3 md:mb-4`} aria-hidden="true" />
                  <h3 className="font-semibold text-gray-800 mb-2 text-base md:text-lg">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white" aria-labelledby="services-preview-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 md:mb-14 flex flex-col items-center">
            
            <Link
              to="/szolgaltatasok"
              className="group relative inline-flex items-center justify-center px-8 py-4 mb-8 text-lg font-bold text-white bg-gradient-to-r from-orange-400 via-pink-500 to-orange-500 rounded-full overflow-hidden shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 no-underline"
            >
              <div className="absolute inset-0 bg-white/20 group-hover:bg-transparent transition-colors duration-300"></div>
              <span className="relative flex items-center gap-2">
                Mi is az a Mosolytorna?
                <img 
                  src="/favicon.svg" 
                  alt="Mosolytorna" 
                  className="w-6 h-6 object-contain brightness-0 invert drop-shadow-md" 
                  aria-hidden="true" 
                />
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <h2
              id="services-preview-heading"
              className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-800 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Szolgáltatásaim
            </h2>

            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
              Személyre szabott terápiák és egyéni megközelítés minden gyermek és felnőtt számára.
            </p>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10 list-none p-0 m-0">
            {SERVICES_PAGE.serviceCards.map((card, i) => (
              <li key={i}>
                <Link
                  to={card.href}
                  data-testid={`service-preview-card-${i}`}
                  className={`block bg-gradient-to-br ${card.gradient} rounded-3xl p-5 md:p-6 border-2 ${card.border} hover:shadow-lg transition-all duration-200 hover:-translate-y-1 group no-underline`}
                >
                  <h3 className="font-semibold text-gray-800 mb-2 text-base md:text-lg group-hover:text-orange-700 transition-colors duration-200">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3 md:mb-4">{card.desc}</p>
                  <span className="text-orange-500 text-sm font-medium flex items-center gap-1" aria-hidden="true">
                    Tovább <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="text-center">
            <Link
              to="/szolgaltatasok"
              data-testid="all-services-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-medium transition-colors duration-200 shadow-md hover:shadow-lg no-underline"
            >
              Összes szolgáltatás
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="py-12 md:py-20 bg-white" aria-labelledby="about-teaser-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2
                id="about-teaser-heading"
                className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-800 mb-4 md:mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Rólam
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                A <strong>Bárczi Gusztáv Gyógypedagógiai Tanárképző Főiskolán</strong> végeztem
                gyógypedagógiai tanárként. Aktívan <strong>2004 óta foglalkozom logopédusként</strong> kisgyermekekkel.
              </p>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6 md:mb-8">
                Hagyományos és innovatív módszerek ötvözésével, szeretetteljes környezetben segítem
                a gyermekeket és felnőtteket a legjobb eredmények elérésében.
              </p>
              <Link
                to="/rolam"
                data-testid="about-teaser-btn"
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-orange-400 text-orange-600 rounded-full font-medium hover:bg-orange-50 transition-colors duration-200 no-underline"
              >
                Ismerjen meg jobban
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="order-1 md:order-2 flex justify-center">
              <div className="relative w-full max-w-sm">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-200 to-orange-200 rounded-3xl blur-2xl opacity-40 scale-105" aria-hidden="true" />
                <img
                  src="/profile.png"
                  alt="Peredi Krisztina logopédus"
                  className="relative w-full max-w-xs sm:max-w-sm mx-auto h-64 sm:h-72 md:h-80 rounded-3xl object-cover shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
